import { useCallback, useEffect, useMemo, useState } from 'react';

import { Link, useNavigate, useParams } from 'react-router-dom';

import { DownOutlined, RightOutlined } from '@ant-design/icons';
import { Flex, Table, Typography } from 'antd';
import type { TableColumnsType } from 'antd';

import { useDebounce } from '@/shared/hooks/useDebounce';
import { usePagination } from '@/shared/hooks/usePagination';
import { useSearchLengthValidation } from '@/shared/hooks/useSearchLengthValidation';
import { EMPTY_MESSAGES, OWNER_AVATAR_SIZES, PLACEHOLDER_MESSAGES, VALIDATION_MESSAGES } from '@/shared/lib/constants';
import { calculateFqnDepth } from '@/shared/lib/format/fqn';
import { AssetDescription } from '@/shared/ui/AssetDescription';
import { BadgeList } from '@/shared/ui/BadgeList';
import { ColumnVisibilityButton, useColumnVisibility } from '@/shared/ui/ColumnVisibilityButton';
import { EllipsisText } from '@/shared/ui/EllipsisText';
import { OwnerAvatars } from '@/shared/ui/OwnerAvatars';
import { SearchInput } from '@/shared/ui/SearchInput';
import { TableEmpty } from '@/shared/ui/TableEmpty';

import { useDisplaySettingsStore } from '@/entities/display-settings';
import type { GlossaryTermItem } from '@/entities/glossary/domain';

import { getGlossaryTermTreeApi } from '@/features/glossary/api/getGlossaryTermApi';
import { resolveGlossaryTermListRowHref } from '@/features/glossary/lib/resolveGlossaryTermListRowHref';
import { useGlossaryTerm } from '@/features/glossary/model/useGlossaryTerm';
import { useSearchGlossaryTerm } from '@/features/glossary/model/useSearchGlossaryTerm';

import styles from './GlossaryTable.module.css';

const { Text } = Typography;

type TableRowData = {
  key: string;
  id: string;
  name: string;
  displayName: string;
  description?: string;
  reviewers?: Array<{ name: string }>;
  status: string;
  synonyms?: string[];
  relatedTerms?: Array<{ displayName: string; fullyQualifiedName: string }>;
  owners?: Array<{ name: string }>;
  childrenCount?: number;
  fullyQualifiedName: string;
  children?: TableRowData[];
  depth: number;
};

type ColumnKey = 'displayName' | 'description' | 'synonyms' | 'relatedTerms' | 'owners';

const TREE_INDENT_SIZE = 48;
const MAX_EXPAND_DEPTH = 2;

const convertToTableRow = (term: GlossaryTermItem): TableRowData => {
  const depth = calculateFqnDepth(term.fullyQualifiedName);
  const hasChildren = (term.childrenCount ?? 0) > 0;
  const canExpand = hasChildren && depth < MAX_EXPAND_DEPTH;

  return {
    key: term.id,
    id: term.id,
    name: term.name,
    displayName: term.displayName || term.name,
    description: term.description,
    reviewers: term.reviewers,
    status: term.status,
    synonyms: term.synonyms,
    relatedTerms: term.relatedTerms,
    owners: term.owners,
    childrenCount: term.childrenCount,
    fullyQualifiedName: term.fullyQualifiedName,
    children: canExpand ? [] : undefined,
    depth,
  };
};

export const GlossaryTable = () => {
  const { termCategory } = useParams<{ termCategory?: string }>();
  const navigate = useNavigate();
  const showOwners = useDisplaySettingsStore((state) => state.showOwners);

  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const shouldUseSearch = debouncedSearchQuery.trim().length > 0;

  const { validateLength, handleCompositionStart, handleCompositionEnd } = useSearchLengthValidation({
    maxLength: 256,
    warningMessage: VALIDATION_MESSAGES.GLOSSARY_SEARCH_MAX_LENGTH,
    debounceMs: 1000,
  });

  const resetTableState = useCallback(() => {
    setExpandedRowKeys([]);
    setLoadedKeys(new Set());
  }, []);

  const handleSearch = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const validatedValue = validateLength(e.target.value);
      setSearchQuery(validatedValue);
      resetTableState();
    },
    [resetTableState, validateLength]
  );

  const { data: listData, isLoading } = useGlossaryTerm({
    directChildrenOf: termCategory,
  });

  const { data: searchData, isLoading: isSearchLoading } = useSearchGlossaryTerm({
    q: debouncedSearchQuery,
    glossaryFqn: termCategory,
    offset: 0,
    enabled: shouldUseSearch,
  });

  const [tableData, setTableData] = useState<TableRowData[]>([]);
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);
  const [loadedKeys, setLoadedKeys] = useState<Set<string>>(new Set());

  const DEFAULT_COLUMN_ORDER = useMemo<ColumnKey[]>(() => {
    const columns: ColumnKey[] = ['displayName', 'description', 'synonyms', 'relatedTerms'];
    if (showOwners) columns.push('owners');
    return columns;
  }, [showOwners]);

  const DEFAULT_VISIBLE_COLUMNS = useMemo<ColumnKey[]>(() => {
    return DEFAULT_COLUMN_ORDER;
  }, [DEFAULT_COLUMN_ORDER]);

  const allTerms = useMemo(() => {
    if (shouldUseSearch) return searchData?.data ?? [];
    return listData?.data ?? [];
  }, [shouldUseSearch, searchData, listData]);

  const dataSource = useMemo<TableRowData[]>(() => {
    return allTerms.filter((term) => term.status === 'Approved').map((term) => convertToTableRow(term));
  }, [allTerms]);

  const { paginationConfig } = usePagination({
    dataLength: dataSource.length,
    viewportOffset: 548,
  });

  const updateTableData = useCallback((list: TableRowData[], key: string, children: TableRowData[]): TableRowData[] => {
    return list.map((row) => {
      if (row.key === key) {
        return { ...row, children };
      }

      if (row.children && row.children.length > 0) {
        return { ...row, children: updateTableData(row.children, key, children) };
      }
      return row;
    });
  }, []);

  const onLoadChildren = useCallback(
    async (record: TableRowData) => {
      const { key, fullyQualifiedName, childrenCount } = record;

      if (loadedKeys.has(key as string)) {
        return;
      }

      if (!childrenCount || childrenCount === 0) {
        return;
      }

      try {
        const response = await getGlossaryTermTreeApi({
          directChildrenOf: fullyQualifiedName,
        });

        const childRows = response.data.map((term) => convertToTableRow(term));

        setTableData((origin) => {
          const updated = updateTableData(origin, key as string, childRows);
          return updated;
        });
        setLoadedKeys((prev) => new Set(prev).add(key as string));
      } catch (error) {
        console.error('Failed to load children:', error);
      }
    },
    [loadedKeys, updateTableData]
  );

  const handleExpand = useCallback(
    (expanded: boolean, record: TableRowData) => {
      if (expanded && record.childrenCount && record.childrenCount > 0) {
        if (!loadedKeys.has(record.key)) {
          onLoadChildren(record);
        }
      }
    },
    [loadedKeys, onLoadChildren]
  );

  const handleExpandedRowsChange = useCallback((expandedKeys: readonly React.Key[]) => {
    setExpandedRowKeys([...expandedKeys]);
  }, []);

  const hasData = tableData.length > 0;

  const allColumnDefinitions: Partial<Record<ColumnKey, TableColumnsType<TableRowData>[number]>> = useMemo(() => {
    const cols: Partial<Record<ColumnKey, TableColumnsType<TableRowData>[number]>> = {
      displayName: {
        title: '용어명',
        dataIndex: 'displayName',
        key: 'displayName',
        width: 200,
        render: (text: string, record: TableRowData) => (
          <Link
            className={styles.termNameLink}
            to={resolveGlossaryTermListRowHref(termCategory, record.fullyQualifiedName)}>
            <EllipsisText text={text} tooltipPlacement="topLeft" />
          </Link>
        ),
      },
      description: {
        title: '설명',
        dataIndex: 'description',
        key: 'description',
        render: (description?: string) => <AssetDescription description={description} mode="expandable" />,
        width: hasData ? 450 : 'auto',
      },
      synonyms: {
        title: '동의어',
        dataIndex: 'synonyms',
        key: 'synonyms',
        width: 150,
        render: (synonyms?: string[]) => {
          if (!synonyms || synonyms.length === 0) return <Text type="secondary">-</Text>;

          const badgeItems = synonyms.map((synonym, index) => ({
            tagFQN: `synonym-${index}`,
            name: synonym,
            displayName: synonym,
          }));

          return <BadgeList items={badgeItems} isEllipsis={false} iconName="book" />;
        },
      },
      relatedTerms: {
        title: '관련 용어',
        dataIndex: 'relatedTerms',
        key: 'relatedTerms',
        width: 200,
        render: (relatedTerms?: Array<{ displayName: string; fullyQualifiedName: string }>) => {
          if (!relatedTerms || relatedTerms.length === 0) return <Text type="secondary">-</Text>;

          const badgeItems = relatedTerms.map((term) => ({
            tagFQN: term.fullyQualifiedName,
            name: term.displayName,
            displayName: term.displayName,
          }));

          const handleTermClick = (fqn: string) => {
            navigate(resolveGlossaryTermListRowHref(termCategory, fqn));
          };

          return <BadgeList items={badgeItems} isEllipsis={false} onItemClick={handleTermClick} iconName="book" />;
        },
      },
    };

    if (showOwners) {
      cols.owners = {
        title: '소유자',
        dataIndex: 'owners',
        key: 'owners',
        width: 150,
        render: (owners?: Array<{ name: string }>) => {
          if (!owners || owners.length === 0) return '-';
          const formattedOwners = owners.map((owner) => ({
            id: owner.name,
            name: owner.name,
            displayName: owner.name,
            type: 'user' as const,
          }));
          return <OwnerAvatars owners={formattedOwners} size={OWNER_AVATAR_SIZES.TABLE} />;
        },
      };
    }

    return cols;
  }, [navigate, hasData, showOwners, termCategory]);

  const allColumnsMapForVisibility = useMemo(
    () =>
      Object.entries(allColumnDefinitions).reduce(
        (acc, [key, col]) => {
          if (col) {
            return {
              ...acc,
              [key]: { title: col.title as string },
            };
          }
          return acc;
        },
        {} as Record<ColumnKey, { title: string }>
      ),
    [allColumnDefinitions]
  );

  const { state } = useColumnVisibility<ColumnKey>({
    defaultColumnOrder: DEFAULT_COLUMN_ORDER,
    defaultVisibleColumns: DEFAULT_VISIBLE_COLUMNS,
    allColumnsMap: allColumnsMapForVisibility,
  });

  const { columnOrder, visibleColumns } = state;

  const visibleColumnsData = useMemo<TableColumnsType<TableRowData>>(() => {
    return columnOrder
      .filter((key) => Object.hasOwn(allColumnDefinitions, key) && visibleColumns.includes(key))
      .map((key) => allColumnDefinitions[key])
      .filter((col): col is TableColumnsType<TableRowData>[number] => col !== undefined);
  }, [columnOrder, visibleColumns, allColumnDefinitions]);

  useEffect(() => {
    resetTableState();
    setSearchQuery('');
  }, [termCategory, resetTableState]);

  useEffect(() => {
    setTableData(dataSource);
  }, [dataSource]);

  return (
    <Flex vertical className={styles.tablesTab}>
      <div className={styles.toolbar}>
        <SearchInput
          placeholder={PLACEHOLDER_MESSAGES.GLOSSARY_SEARCH}
          value={searchQuery}
          onChange={handleSearch}
          onCompositionStart={handleCompositionStart}
          onCompositionEnd={handleCompositionEnd}
          maxLength={256}
        />
        <div className={styles.toolbarRight}>
          <ColumnVisibilityButton
            defaultColumnOrder={DEFAULT_COLUMN_ORDER}
            defaultVisibleColumns={DEFAULT_VISIBLE_COLUMNS}
            allColumnsMap={allColumnsMapForVisibility}
          />
        </div>
      </div>
      <Table<TableRowData>
        columns={visibleColumnsData}
        dataSource={visibleColumnsData.length > 0 ? tableData : []}
        pagination={paginationConfig}
        scroll={{ x: tableData.length > 0 ? 'max-content' : undefined }}
        expandable={{
          expandedRowKeys,
          onExpandedRowsChange: handleExpandedRowsChange,
          onExpand: handleExpand,
          childrenColumnName: 'children',
          rowExpandable: (record) => {
            const hasChildren = (record.childrenCount ?? 0) > 0;
            const canExpand = hasChildren && record.depth < MAX_EXPAND_DEPTH;
            return canExpand;
          },
          indentSize: TREE_INDENT_SIZE,
          expandIcon: ({ expanded, onExpand, record }) => {
            const hasChildren = (record.childrenCount ?? 0) > 0;
            const canExpand = hasChildren && record.depth < MAX_EXPAND_DEPTH;
            if (!canExpand) return null;
            return expanded ? (
              <DownOutlined onClick={(e) => onExpand(record, e)} className={styles.expandIcon} />
            ) : (
              <RightOutlined onClick={(e) => onExpand(record, e)} className={styles.expandIcon} />
            );
          },
        }}
        className={styles.tablesTable}
        sticky={true}
        locale={{
          emptyText: (
            <TableEmpty
              searchQuery={searchQuery}
              isVisibleColumns={visibleColumnsData.length === 0}
              description={EMPTY_MESSAGES.NO_GLOSSARY}
            />
          ),
        }}
        loading={isLoading || isSearchLoading}
      />
    </Flex>
  );
};
